import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PhotoIcon,
  XCircleIcon,
  PlusCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAppData } from '../../contexts/AppDataContext';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const { productCategories, suppliers, fetchAllData, addProduct, updateProduct, products } = useAppData();
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Form state - using strings for all fields to avoid type conversion issues
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    barcode: '',
    category: '',
    price: '',
    costPrice: '',
    taxRate: '19',
    description: '',
    stock: '',
    minStockLevel: '',
    maxStock: '',
    image: null,
    isActive: true,
    supplier: '',
    supplierId: ''
  });
  
  // Calculated price including tax
  const [priceTTC, setPriceTTC] = useState('');
  
  // Image preview state
  const [imagePreview, setImagePreview] = useState(null);

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Load data when component mounts
  useEffect(() => {
    // Fetch categories and suppliers
    fetchAllData();
    
    // If editing, load the product data
    if (isEditMode) {
      loadProductData();
    }
  }, [isEditMode, id]);

  // Calculate price with tax whenever price or taxRate changes
  useEffect(() => {
    calculatePriceTTC();
  }, [formData.price, formData.taxRate]);

  // Function to calculate price including tax
  const calculatePriceTTC = () => {
    try {
      const price = parseFloat(formData.price);
      const taxRate = parseFloat(formData.taxRate || '0');
      
      if (!isNaN(price) && !isNaN(taxRate)) {
      const priceTTC = price * (1 + taxRate / 100);
      setPriceTTC(priceTTC.toFixed(2));
    } else {
      setPriceTTC('');
    }
    } catch (error) {
      console.error("Error calculating price TTC:", error);
      setPriceTTC('');
    }
  };

  // Load product data from context or localStorage
  const loadProductData = () => {
    setIsLoading(true);
    
    try {
      // First try to get the product directly from the context
      const contextProduct = products.find(p => p.id === id);
      
      if (contextProduct) {
        // Format numeric values as strings to avoid type issues
        const updatedFormData = {
          ...contextProduct,
          price: contextProduct.price?.toString() || '',
          costPrice: contextProduct.cost?.toString() || '',
          stock: contextProduct.stock?.toString() || '',
          minStockLevel: (contextProduct.minStockLevel || contextProduct.minStock)?.toString() || '',
          maxStock: contextProduct.maxStock?.toString() || '',
          isActive: contextProduct.isActive !== false, // Default to true if not specified
          supplier: contextProduct.supplier || '',
          supplierId: contextProduct.supplierId || ''
        };
        
        // If the product uses the new format with productSuppliers array
        // Extract the first supplier for backwards compatibility
        if (contextProduct.productSuppliers && Array.isArray(contextProduct.productSuppliers) && contextProduct.productSuppliers.length > 0) {
          const mainSupplier = contextProduct.productSuppliers[0];
          updatedFormData.supplier = mainSupplier.supplierName;
          updatedFormData.supplierId = mainSupplier.supplierId;
          updatedFormData.price = mainSupplier.price?.toString() || '';
        }
        
        setFormData(updatedFormData);
        
        // Set image preview if available
        if (contextProduct.image) {
          setImagePreview(contextProduct.image);
        } else if (contextProduct.imageUrl) {
          setImagePreview(contextProduct.imageUrl);
        }
        
        console.log('Product loaded successfully from context');
        setIsLoading(false);
        return;
      }
      
      // If not found in context, try localStorage as fallback
      console.log('Product not found in context, trying localStorage...');
      let savedProductsJSON = localStorage.getItem('stockpro_products');
      if (!savedProductsJSON) {
        savedProductsJSON = localStorage.getItem('products');
      }
      
      if (!savedProductsJSON) {
        toast.error('Aucun produit trouvé');
        navigate('/products');
        return;
      }
      
      const savedProducts = JSON.parse(savedProductsJSON);
      const product = savedProducts.find(p => p.id === id);
      
      if (!product) {
        toast.error('Produit non trouvé');
        navigate('/products');
          return;
        }
      
      // Format numeric values as strings to avoid type issues
      const updatedFormData = {
        ...product,
        price: product.price?.toString() || '',
        costPrice: product.cost?.toString() || '',
        stock: product.stock?.toString() || '',
        minStockLevel: (product.minStockLevel || product.minStock)?.toString() || '',
        maxStock: product.maxStock?.toString() || '',
        isActive: product.isActive !== false, // Default to true if not specified
        supplier: product.supplier || '',
        supplierId: product.supplierId || ''
      };
      
      // If the product uses the new format with productSuppliers array
      // Extract the first supplier for backwards compatibility
      if (product.productSuppliers && Array.isArray(product.productSuppliers) && product.productSuppliers.length > 0) {
        const mainSupplier = product.productSuppliers[0];
        updatedFormData.supplier = mainSupplier.supplierName;
        updatedFormData.supplierId = mainSupplier.supplierId;
        updatedFormData.price = mainSupplier.price?.toString() || '';
      }
      
      setFormData(updatedFormData);
      
      // Set image preview if available
      if (product.image) {
        setImagePreview(product.image);
      } else if (product.imageUrl) {
        setImagePreview(product.imageUrl);
      }
      
      console.log('Product loaded successfully from localStorage');
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Erreur lors du chargement du produit');
    } finally {
      setIsLoading(false);
    }
  };

  // Generic input change handler for all form fields
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Use the appropriate value based on input type
    const newValue = type === 'checkbox' ? checked : value;
    
    // Special handling for supplier selection
    if (name === 'supplierId' && value) {
      const selectedSupplier = suppliers.find(s => s.id === value);
      if (selectedSupplier) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          supplier: selectedSupplier.name
        }));
        return;
      }
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Clear any error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  // Handle supplier selection within the regular handleInputChange function
  
  // These multi-supplier functions have been removed

  // Handle image upload
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file size (limit to 2MB to avoid localStorage issues)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('L\'image est trop volumineuse. Maximum 2MB.');
        return;
      }
      
      // Create URL for preview
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      
      // Convert image to base64 for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        try {
          const base64String = reader.result;
          
          // Estimate the size of the base64 string (roughly 4/3 of the file size)
          const estimatedSize = Math.round((base64String.length * 3) / 4);
          if (estimatedSize > 2 * 1024 * 1024) {
            toast.error('L\'image convertie est trop volumineuse. Choisissez une image plus petite.');
            setImagePreview(null);
            return;
          }
          
          // Compress the image if needed
          const img = new Image();
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              // Set canvas dimensions to reduce image size if needed
              const maxDimension = 800;
              let width = img.width;
              let height = img.height;
              
              if (width > height && width > maxDimension) {
                height = Math.round(height * (maxDimension / width));
                width = maxDimension;
              } else if (height > maxDimension) {
                width = Math.round(width * (maxDimension / height));
                height = maxDimension;
              }
              
              canvas.width = width;
              canvas.height = height;
              
              // Draw image on canvas with reduced dimensions
              ctx.drawImage(img, 0, 0, width, height);
              
              // Get compressed base64 string
              const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
      
      setFormData(prev => ({
        ...prev,
                image: compressedBase64
              }));
              
              // Clean up
              URL.revokeObjectURL(imageUrl);
              
              toast.success('Image ajoutée avec succès!');
            } catch (compressError) {
              console.error('Error compressing image:', compressError);
              // Fallback to original base64 if compression fails
              setFormData(prev => ({
                ...prev,
                image: base64String
              }));
      toast.success('Image ajoutée avec succès!');
            }
          };
          
          img.onerror = () => {
            console.error('Error loading image for compression');
            // Fallback to original base64 if loading fails
            setFormData(prev => ({
              ...prev,
              image: base64String
            }));
            toast.success('Image ajoutée avec succès!');
          };
          
          img.src = base64String;
        } catch (error) {
          console.error('Error processing image:', error);
          toast.error('Erreur lors du traitement de l\'image');
          setImagePreview(null);
        }
      };
      reader.onerror = () => {
        toast.error('Erreur lors du chargement de l\'image');
        setImagePreview(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove image
  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      image: null
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name?.trim()) newErrors.name = 'Le nom est requis';
    if (!formData.sku?.trim()) newErrors.sku = 'Le SKU est requis';
    if (!formData.category?.trim()) newErrors.category = 'La catégorie est requise';
    
    // Numeric fields
    if (!formData.price?.trim()) {
      newErrors.price = 'Le prix est requis';
    } else if (isNaN(parseFloat(formData.price))) {
      newErrors.price = 'Le prix doit être un nombre';
    }
    
    if (!formData.costPrice?.trim()) {
      newErrors.costPrice = 'Le prix de revient est requis';
    } else if (isNaN(parseFloat(formData.costPrice))) {
      newErrors.costPrice = 'Le prix de revient doit être un nombre';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isLoading) return;
    
    // Validate form
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create product object with proper type conversion for storage
      const productToSave = {
        ...formData,
        id: formData.id || `${formData.name.slice(0, 3).toUpperCase()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        price: parseFloat(formData.price) || 0,
        cost: parseFloat(formData.costPrice) || 0, // Map costPrice to cost for compatibility
        stock: parseInt(formData.stock) || 0,
        minStock: parseInt(formData.minStockLevel) || 0, // Map minStockLevel to minStock for compatibility
        minStockLevel: parseInt(formData.minStockLevel) || 0,
        maxStock: parseInt(formData.maxStock) || 0,
        supplier: formData.supplier,
        supplierId: formData.supplierId
      };
      
      // Handle image - the image is already a base64 string from handleImageChange
      try {
        if (formData.image && typeof formData.image === 'string' && formData.image.startsWith('data:')) {
          productToSave.image = formData.image;
          productToSave.imageUrl = formData.image; // For backwards compatibility
        } else if (imagePreview) {
          // If we have an imagePreview but no formData.image, try to use the preview
          productToSave.image = imagePreview;
          productToSave.imageUrl = imagePreview;
        }
      } catch (imageError) {
        console.error('Error processing image during save:', imageError);
        // Continue without the image if there's an error
      }
      
      // Save product directly using the AppData context instead of localStorage
      try {
        // Use the appropriate function based on edit mode
        if (isEditMode) {
          if (updateProduct) {
            console.log('Updating product with ID:', productToSave.id);
            console.log('Product data to save:', productToSave);
            await updateProduct(productToSave);
            // Force refresh data
            await fetchAllData();
          } else {
            // Fallback to localStorage if context function is not available
            saveProductToLocalStorage(productToSave);
          }
        } else {
          if (addProduct) {
            await addProduct(productToSave);
          } else {
            // Fallback to localStorage if context function is not available
      saveProductToLocalStorage(productToSave);
          }
        }
      
      // Show success message
      toast.success(isEditMode ? 'Produit mis à jour avec succès!' : 'Produit ajouté avec succès!');
      
      // Navigate back to products list
      setTimeout(() => {
        navigate('/products');
      }, 500);
      } catch (saveError) {
        console.error('Error saving product through context:', saveError);
        // Try fallback to localStorage
        saveProductToLocalStorage(productToSave);
        
        // Show success message for the fallback
        toast.success(isEditMode ? 'Produit mis à jour avec succès (mode local)!' : 'Produit ajouté avec succès (mode local)!');
        
        // Navigate back to products list
        setTimeout(() => {
          navigate('/products');
        }, 500);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Une erreur est survenue lors de l\'enregistrement du produit');
      setIsLoading(false);
    }
  };

  // Save product to localStorage
  const saveProductToLocalStorage = (product) => {
    try {
      // Get existing products - try both storage keys
      let existingProductsJSON = localStorage.getItem('stockpro_products');
      if (!existingProductsJSON) {
        existingProductsJSON = localStorage.getItem('products');
      }
      
      let allProducts = [];
        if (existingProductsJSON) {
        try {
          allProducts = JSON.parse(existingProductsJSON);
          // Ensure it's an array
          if (!Array.isArray(allProducts)) {
            console.warn('Products in localStorage is not an array, resetting');
            allProducts = [];
          }
        } catch (parseError) {
          console.error('Error parsing products from localStorage:', parseError);
          allProducts = [];
        }
      }
      
      // Create a safe copy of the product without circular references
      const safeProduct = JSON.parse(JSON.stringify(product));
      
      // Update or add product
      if (isEditMode) {
        const index = allProducts.findIndex(p => p.id === safeProduct.id);
        if (index >= 0) {
          allProducts[index] = safeProduct;
        } else {
          allProducts.push(safeProduct);
        }
      } else {
        allProducts.push(safeProduct);
      }
      
      // Debug log
      console.log('Product being saved:', safeProduct);
      
      // Convert to JSON string once
      const productsJSON = JSON.stringify(allProducts);
      
      // Save to both storage keys for compatibility
      localStorage.setItem('stockpro_products', productsJSON);
      localStorage.setItem('products', productsJSON);
      
      // Update last modified timestamp
      localStorage.setItem('products_last_updated', new Date().toISOString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      toast.error('Erreur lors de l\'enregistrement du produit');
      throw error;
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    navigate('/products');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            className="p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-white" />
          </button>
          <h2 className="text-xl font-semibold text-white">
            {isEditMode ? 'Modifier le produit' : 'Nouveau produit'}
          </h2>
        </div>
      </div>

      {/* Main Form Container */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Product Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-1">
                  Nom du produit*
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                    value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full bg-white/5 border ${errors.name ? 'border-red-500' : 'border-white/10'} rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500`}
                  placeholder="Nom du produit"
                />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
              </div>

              {/* SKU & Barcode */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="sku" className="block text-sm font-medium text-white mb-1">
                    SKU*
                  </label>
                  <input
                    type="text"
                    id="sku"
                    name="sku"
                      value={formData.sku}
                    onChange={handleInputChange}
                    className={`w-full bg-white/5 border ${errors.sku ? 'border-red-500' : 'border-white/10'} rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500`}
                    placeholder="SKU-001"
                  />
                  {errors.sku && <p className="mt-1 text-xs text-red-500">{errors.sku}</p>}
                </div>
                <div>
                  <label htmlFor="barcode" className="block text-sm font-medium text-white mb-1">
                    Code-barres
                  </label>
                  <input
                    type="text"
                    id="barcode"
                    name="barcode"
                      value={formData.barcode}
                    onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="123456789"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-white mb-1">
                  Catégorie*
                </label>
                <select
                  id="category"
                  name="category"
                    value={formData.category}
                  onChange={handleInputChange}
                  className={`w-full bg-white/5 border ${errors.category ? 'border-red-500' : 'border-white/10'} rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500`}
                >
                  <option value="">Sélectionnez une catégorie</option>
                  {productCategories && productCategories.length > 0 ? (
                    productCategories.map((category, index) => (
                      <option key={index} value={category}>
                        {category}
                      </option>
                    ))
                  ) : (
                    <>
                        <option value="supplements">Suppléments</option>
                  <option value="Catégorie 1">Catégorie 1</option>
                  <option value="Catégorie 2">Catégorie 2</option>
                  <option value="Catégorie 3">Catégorie 3</option>
                    </>
                  )}
                </select>
                {errors.category && <p className="mt-1 text-xs text-red-500">{errors.category}</p>}
              </div>

              {/* Supplier Selection */}
              <div>
                <label htmlFor="supplierId" className="block text-sm font-medium text-white mb-1">
                  Fournisseur
                </label>
                <select
                  id="supplierId"
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={handleInputChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Sélectionnez un fournisseur</option>
                  {suppliers && suppliers.length > 0 ? (
                    suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                      </option>
                    ))
                  ) : (
                    <>
                        <option value="SUP-001">Algérie Papier (SUP-001)</option>
                        <option value="SUP-002">Tech Informatique (SUP-002)</option>
                        <option value="SUP-003">Office Plus (SUP-003)</option>
                        <option value="SUP-004">MeublePro (SUP-004)</option>
                        <option value="SUP-005">Imprimerie Moderne (SUP-005)</option>
                        <option value="SUP-006">Électro Algérie (SUP-006)</option>
                        <option value="SUP-007">Pro Nettoyage (SUP-007)</option>
                    </>
                  )}
                </select>
                <p className="mt-1 text-xs text-white/60">Fournisseur du produit</p>
              </div>

              {/* Price, Cost Price, Tax Rate */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-white mb-1">
                    Prix (DA)*
                  </label>
                  <input
                    type="text"
                    id="price"
                    name="price"
                      value={formData.price}
                    onChange={handleInputChange}
                    className={`w-full bg-white/5 border ${errors.price ? 'border-red-500' : 'border-white/10'} rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500`}
                    placeholder="0.00"
                  />
                  {errors.price && <p className="mt-1 text-xs text-red-500">{errors.price}</p>}
                </div>
                <div>
                  <label htmlFor="costPrice" className="block text-sm font-medium text-white mb-1">
                    Prix de revient (DA)*
                  </label>
                  <input
                    type="text"
                    id="costPrice"
                    name="costPrice"
                      value={formData.costPrice}
                    onChange={handleInputChange}
                    className={`w-full bg-white/5 border ${errors.costPrice ? 'border-red-500' : 'border-white/10'} rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500`}
                    placeholder="0.00"
                  />
                  {errors.costPrice && <p className="mt-1 text-xs text-red-500">{errors.costPrice}</p>}
                </div>
                <div>
                  <label htmlFor="taxRate" className="block text-sm font-medium text-white mb-1">
                    TVA (%)
                  </label>
                  <select
                    id="taxRate"
                    name="taxRate"
                      value={formData.taxRate}
                    onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="0">0</option>
                    <option value="9">9</option>
                    <option value="19">19</option>
                  </select>
                </div>
              </div>
              
              {/* Price including tax (calculated) */}
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Prix TTC (avec TVA)
                </label>
                <div className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white">
                  {priceTTC ? `${priceTTC} DA` : '0.00 DA'}
                </div>
                <p className="mt-1 text-xs text-white/60">Calculé automatiquement en fonction du prix et du taux de TVA</p>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-6">
              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-white mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                    value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Description du produit..."
                />
              </div>

              {/* Stock Quantity, Min & Max Stock Levels */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="stock" className="block text-sm font-medium text-white mb-1">
                    Quantité en stock
                  </label>
                  <input
                    type="text"
                    id="stock"
                    name="stock"
                      value={formData.stock}
                      onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label htmlFor="minStockLevel" className="block text-sm font-medium text-white mb-1">
                    Niveau minimal
                  </label>
                  <input
                    type="text"
                    id="minStockLevel"
                    name="minStockLevel"
                      value={formData.minStockLevel}
                      onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label htmlFor="maxStock" className="block text-sm font-medium text-white mb-1">
                    Niveau maximal
                  </label>
                  <input
                    type="text"
                    id="maxStock"
                    name="maxStock"
                      value={formData.maxStock}
                      onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Image du produit
                </label>
                {imagePreview ? (
                  <div className="relative mt-1">
                    <div className="relative w-full h-48 overflow-hidden rounded-xl">
                      <img 
                        src={imagePreview} 
                        alt="Product preview" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                          onClick={handleRemoveImage}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        aria-label="Remove image"
                      >
                        <XCircleIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-white/60">
                      Cliquez sur X pour supprimer l'image
                    </p>
                  </div>
                ) : (
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-white/10 border-dashed rounded-xl">
                  <div className="space-y-1 text-center">
                    <PhotoIcon className="mx-auto h-12 w-12 text-white/40" />
                    <div className="flex text-sm text-white/60">
                      <label
                        htmlFor="image-upload"
                        className="relative cursor-pointer bg-white/10 rounded-md font-medium text-white hover:text-white/80 px-3 py-2"
                      >
                        <span>Télécharger un fichier</span>
                        <input
                          id="image-upload"
                          name="image-upload"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleImageChange}
                        />
                      </label>
                      <p className="pl-1 pt-2">ou glisser-déposer</p>
                    </div>
                    <p className="text-xs text-white/60">
                      PNG, JPG, GIF jusqu'à 10MB
                    </p>
                  </div>
                </div>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                    checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-white/30 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-white">
                  Produit actif
                </label>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-xl"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-xl flex items-center"
            >
              {isLoading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isEditMode ? 'Mettre à jour' : 'Enregistrer'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default ProductForm; 